<section class="flex flex-col gap-1.5">
  <button
    class="inline-flex items-center gap-1 self-start rounded-full border border-neutral-200 px-2.5 py-1 text-sm font-semibold text-neutral-600 transition-colors duration-150 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
    type="button"
    [attr.aria-expanded]="open()"
    [matTooltip]="hintKey() | translate"
    (click)="toggle()"
  >
    <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5! text-neutral-400">history</mat-icon>
    {{ titleKey() | translate }}
    <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5! text-neutral-400">{{ open() ? "expand_less" : "expand_more" }}</mat-icon>
  </button>

  @if (open()) {
    @if (loading()) {
      <span class="px-1 text-sm text-neutral-400">{{ "general.label.loading" | translate }}</span>
    } @else if (failed()) {
      <guarantees-callout tone="error">
        <span class="text-sm text-error-700">{{ "guaranteeV2.message.historyLoadError" | translate }}</span>
        <button
          class="rounded text-sm font-semibold text-error-700 hover:underline focus-visible:ring-2 focus-visible:ring-error-500/40 focus-visible:outline-none"
          type="button"
          (click)="retry()"
        >
          {{ "guaranteeV2.action.retry" | translate }}
        </button>
      </guarantees-callout>
    } @else if (history().length === 0) {
      <span class="px-1 text-sm text-neutral-400">{{ "guaranteeV2.message.historyEmpty" | translate }}</span>
    } @else {
      <!-- Newest first: a reader scans to the change they came for and stops. -->
      <ol class="flex flex-col gap-2">
        @for (entry of history(); track entry.key) {
          <li class="flex flex-col gap-1 border-l-2 border-neutral-200 pl-2">
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <guarantees-chip
                [tone]="entry.operationTone"
                [icon]="entry.operationIcon"
                >{{ entry.operationLabelKey | translate }}</guarantees-chip
              >
              <span class="text-sm text-neutral-500 tabular-nums">{{ entry.timestamp | date: "dd.MM.yy HH:mm" }}</span>
              @if (entry.actorName | guaranteesActorName; as name) {
                <span class="min-w-0 truncate text-sm text-neutral-500">&middot; {{ name }}</span>
              }
              <span
                class="ml-auto shrink-0 text-xs leading-4 text-neutral-300 tabular-nums"
                [matTooltip]="'guaranteeV2.message.historyVersionHint' | translate: { version: entry.version }"
                >v{{ entry.version }}</span
              >
            </div>

            @if (entry.rows.length > 0) {
              <!-- Field beside value in aligned columns: a run of changes is read down the names. -->
              <ul class="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-2 gap-y-0.5">
                @for (row of entry.rows; track row.key) {
                  <li class="col-span-2 grid grid-cols-subgrid">
                    <span
                      class="min-w-0 truncate text-xs leading-5 font-medium tracking-wide"
                      [class]="row.unnamed ? 'font-mono text-neutral-300' : 'text-neutral-400 uppercase'"
                      [matTooltip]="row.label"
                    >
                      {{ row.label }}
                      @if (row.ordinal) {
                        <span class="tabular-nums"> №{{ row.ordinal }}</span>
                      }
                    </span>

                    <span
                      class="min-w-0 text-sm break-words"
                      [class]="row.valueClass"
                    >
                      @switch (row.kind) {
                        @case ("added") {
                          <span class="font-medium">{{ "guaranteeV2.message.historyAdded" | translate }}</span>
                          {{ row.summary }}
                        }
                        @case ("removed") {
                          <span class="font-medium">{{ "guaranteeV2.message.historyRemoved" | translate }}</span>
                          {{ row.summary }}
                        }
                        @default {
                          <span class="text-neutral-400 line-through">{{ row.before }}</span>
                          <span class="text-neutral-300"> → </span>
                          <span>{{ row.after }}</span>
                        }
                      }
                    </span>
                  </li>
                }
              </ul>
            } @else if (!entry.isEarliest) {
              <!-- A write that only restamped the record: the version is real, the change is not. -->
              <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.historyNoFields" | translate }}</span>
            }
          </li>
        }
      </ol>
    }
  }
</section>
